import { useCallback, useMemo, useRef, useState } from 'react';
import {
  DataEditor,
  GridCellKind,
  type DataEditorRef,
  type EditableGridCell,
  type GridCell,
  type GridColumn,
  type Item,
} from '@glideapps/glide-data-grid';
import '@glideapps/glide-data-grid/dist/index.css';
import { Button, Card, CardContent, CardHeader, CardTitle } from '@produzionejb/ui';
import { generateMockMpgRows, KNOWN_LOT_CODES, type MpgRow } from './mock-data';

type ValidationFlag = 'ok' | 'warn-unknown-lot' | 'error-qty';

interface PocRow extends MpgRow {
  qtyFlag: ValidationFlag;
  lotFlag: ValidationFlag;
}

function wrapForValidation(rows: MpgRow[]): PocRow[] {
  return rows.map((r) => ({
    ...r,
    qtyFlag: r.qty !== null && r.qty >= 0 ? 'ok' : 'error-qty',
    lotFlag: KNOWN_LOT_CODES.has(r.lotCode) ? 'ok' : 'warn-unknown-lot',
  }));
}

function flagBg(flag: ValidationFlag): string | undefined {
  if (flag === 'error-qty') return '#fee2e2';
  if (flag === 'warn-unknown-lot') return '#fef3c7';
  return undefined;
}

const COLUMNS: GridColumn[] = [
  { title: 'Lotto', id: 'lotCode', width: 160 },
  { title: 'Prodotto', id: 'productName', width: 240 },
  { title: 'Qty (g)', id: 'qty', width: 110 },
  { title: 'Causale', id: 'causale', width: 200 },
  { title: 'Note', id: 'note', width: 260 },
];

const KEYS: Array<keyof MpgRow> = ['lotCode', 'productName', 'qty', 'causale', 'note'];

export function MpgGridPocPage() {
  const editorRef = useRef<DataEditorRef>(null);
  const [rows, setRows] = useState<PocRow[]>(() => wrapForValidation(generateMockMpgRows(1000)));
  const [savedAt, setSavedAt] = useState<Date | null>(null);

  const stats = useMemo(() => {
    let qtyErrors = 0;
    let unknownLots = 0;
    for (const r of rows) {
      if (r.qtyFlag === 'error-qty') qtyErrors += 1;
      if (r.lotFlag === 'warn-unknown-lot') unknownLots += 1;
    }
    return { qtyErrors, unknownLots };
  }, [rows]);

  const getCellContent = useCallback(
    (cell: Item): GridCell => {
      const [col, row] = cell;
      const r = rows[row];
      if (!r) {
        return { kind: GridCellKind.Text, data: '', displayData: '', allowOverlay: true };
      }
      const key = KEYS[col]!;
      const value = r[key];

      if (key === 'qty') {
        return {
          kind: GridCellKind.Number,
          data: value === null || value === undefined ? undefined : (value as number),
          displayData: value === null || value === undefined ? '' : String(value),
          allowOverlay: true,
          themeOverride: flagBg(r.qtyFlag) ? { bgCell: flagBg(r.qtyFlag) } : undefined,
        };
      }
      const display = value === null || value === undefined ? '' : String(value);
      const themeOverride = key === 'lotCode' && flagBg(r.lotFlag)
        ? { bgCell: flagBg(r.lotFlag) }
        : undefined;
      return {
        kind: GridCellKind.Text,
        data: display,
        displayData: display,
        allowOverlay: true,
        themeOverride,
      };
    },
    [rows],
  );

  const onCellEdited = useCallback(
    (cell: Item, newValue: EditableGridCell) => {
      const [col, row] = cell;
      const key = KEYS[col]!;
      setRows((prev) => {
        const next = [...prev];
        const target = next[row];
        if (!target) return prev;
        const updated: PocRow = { ...target };
        if (key === 'qty') {
          const num = newValue.kind === GridCellKind.Number ? newValue.data ?? null : null;
          updated.qty = num;
          updated.qtyFlag = num !== null && num >= 0 ? 'ok' : 'error-qty';
        } else if (newValue.kind === GridCellKind.Text) {
          (updated[key] as unknown as string) = newValue.data;
          if (key === 'lotCode') {
            updated.lotFlag = KNOWN_LOT_CODES.has(newValue.data) ? 'ok' : 'warn-unknown-lot';
          }
        }
        next[row] = updated;
        return next;
      });
      setSavedAt(new Date());
    },
    [],
  );

  function handleAppendRows() {
    setRows((prev) => [...prev, ...wrapForValidation(generateMockMpgRows(50))]);
  }

  function handleResetValidationDemo() {
    setRows((prev) =>
      prev.map((r, i) => {
        if (i % 50 === 0) {
          return { ...r, qty: -1, qtyFlag: 'error-qty' };
        }
        if (i % 37 === 0) {
          return { ...r, lotCode: 'INVALID-LOT', lotFlag: 'warn-unknown-lot' };
        }
        return r;
      }),
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold text-slate-900">POC — Glide Data Grid (MPG)</h2>
        <div className="text-sm text-slate-500">
          {savedAt ? `Salvato ${savedAt.toLocaleTimeString()}` : 'Nessuna modifica'}
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Statistiche validazione</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-4 text-sm">
          <span>
            Totale righe: <strong>{rows.length}</strong>
          </span>
          <span className="text-red-700">
            Qty non valide: <strong>{stats.qtyErrors}</strong>
          </span>
          <span className="text-amber-700">
            Lotti sconosciuti: <strong>{stats.unknownLots}</strong>
          </span>
          <div className="ml-auto flex gap-2">
            <Button variant="secondary" size="sm" onClick={handleAppendRows}>
              +50 righe
            </Button>
            <Button variant="secondary" size="sm" onClick={handleResetValidationDemo}>
              Inietta errori demo
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white" style={{ height: 640 }}>
        <DataEditor
          ref={editorRef}
          columns={COLUMNS}
          rows={rows.length}
          getCellContent={getCellContent}
          onCellEdited={onCellEdited}
          rowMarkers="number"
          width="100%"
          height={640}
          smoothScrollX
          smoothScrollY
          fillHandle
          keybindings={{ search: true }}
          onPaste
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Validazione POC</CardTitle>
        </CardHeader>
        <CardContent className="space-y-1 text-sm text-slate-700">
          <p>
            <strong>C2 (paste 100 righe)</strong>: incolla da Excel/Sheets — righe valide entrano,
            righe errate evidenziate (giallo lotto / rosso qty).
          </p>
          <p>
            <strong>C4 (validazione cella)</strong>: qty negativa → rosso; lotto fuori master →
            giallo. Tooltip via hover (Glide native).
          </p>
          <p>
            <strong>C5 (optimistic UI)</strong>: modifica visibile immediatamente nella grid +
            timestamp "Salvato" aggiornato.
          </p>
          <p className="text-slate-500">
            Decisione tecnica registrata in{' '}
            <code>docs/decisions/0001-grid-library.md</code>. Fallback AG Grid Community se POC fail.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
